import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Phone, Mail, Send, Printer, Smartphone, UserRound, Building2 } from 'lucide-react'
import SectionHeading from '../ui/SectionHeading'
import Button from '../ui/Button'
import { settingsApi, contactApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'
import { parseSettingsResponse, getLocalizedSetting } from '../../utils/settings'
import {
  DEFAULT_CONTACT,
  CONTACT_SUBJECT_KEYS,
  CONTACT_BRANCH_KEYS,
  resolveContactBranch,
} from '../../constants/contact'
import { fadeInUp, viewportOnce } from '../../utils/animations'

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  branch: '',
  message: '',
}

function ContactRow({ icon: Icon, label, children }) {
  return (
    <li className="flex items-start gap-4">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/15">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-sm text-white/70">{label}</div>
        <div className="break-all font-medium">{children}</div>
      </div>
    </li>
  )
}

export default function ContactSection({ isContactPage = false }) {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const [searchParams] = useSearchParams()
  const [settings, setSettings] = useState(DEFAULT_CONTACT)
  const [form, setForm] = useState(EMPTY_FORM)
  const [status, setStatus] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    settingsApi
      .get()
      .then(({ data }) => setSettings({ ...DEFAULT_CONTACT, ...parseSettingsResponse(data, language) }))
      .catch(() => {})
  }, [language])

  useEffect(() => {
    if (!isContactPage) return

    const subjectParam = searchParams.get('subject')
    const branchParam = resolveContactBranch(searchParams)
    const eventParam = searchParams.get('event')

    setForm((prev) => {
      const next = { ...prev }

      if (CONTACT_SUBJECT_KEYS.includes(subjectParam)) {
        next.subject = subjectParam
      } else if (eventParam) {
        next.subject = 'inquiry'
      }

      if (branchParam) {
        next.branch = branchParam
      }

      if (eventParam && !next.message) {
        next.message = t('contact.form.eventMessage', { event: eventParam })
      }

      return next
    })
  }, [isContactPage, searchParams, t])

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => {
      const next = { ...prev, [name]: value }
      if (name === 'subject' && value !== 'visit_appointment') {
        next.branch = ''
      }
      return next
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setStatus(null)
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
        ...(form.branch ? { branch: form.branch } : {}),
      }
      await contactApi.submit(payload)
      setStatus('success')
      setForm(EMPTY_FORM)
    } catch {
      setStatus('error')
    } finally {
      setSubmitting(false)
    }
  }

  const showBranchField = isContactPage
  const branchRequired = form.subject === 'visit_appointment'

  const sadeenLabel =
    language === 'ar' ? settings.sadeen_group_ar || settings.sadeen_group : settings.sadeen_group

  const inputClass =
    'w-full rounded-xl border border-border bg-background px-4 py-3 text-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'

  return (
    <section id="contact" className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 gradient-brand opacity-[0.04]" />
      <div className="relative mx-auto max-w-7xl">
        <SectionHeading
          label={t('contact.label')}
          title={t('contact.title')}
          subtitle={t('contact.subtitle')}
        />

        <div className="grid gap-12 lg:grid-cols-5">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeInUp}
            className="lg:col-span-2"
          >
            <div className="rounded-2xl gradient-brand p-6 text-white shadow-2xl shadow-primary/30 sm:rounded-3xl sm:p-8">
              <h3 className="mb-2 text-xl font-bold">
                {getLocalizedSetting(settings, 'school_name', language) || DEFAULT_CONTACT.school_name}
              </h3>
              <p className="mb-6 text-sm text-white/75">{sadeenLabel}</p>

              <ul className="space-y-5">
                <ContactRow icon={Phone} label={t('contact.info.phone')}>
                  <a href={`tel:${settings.phone}`} className="hover:text-accent">
                    {settings.phone}
                  </a>
                </ContactRow>
                <ContactRow icon={Printer} label={t('contact.info.fax')}>
                  {settings.fax}
                </ContactRow>
                <ContactRow icon={Smartphone} label={t('contact.info.mobile')}>
                  <a href={`tel:${settings.mobile}`} className="hover:text-accent">
                    {settings.mobile}
                  </a>
                </ContactRow>
                <ContactRow icon={Mail} label={t('contact.info.email')}>
                  <a href={`mailto:${settings.email}`} className="hover:text-accent">
                    {settings.email}
                  </a>
                </ContactRow>
                <ContactRow icon={UserRound} label={t('contact.info.americanCounselor')}>
                  <a
                    href={`mailto:${settings.american_program_counselor_email}`}
                    className="hover:text-accent"
                  >
                    {settings.american_program_counselor_email}
                  </a>
                </ContactRow>
                <ContactRow icon={UserRound} label={t('contact.info.internationalDirector')}>
                  <a
                    href={`mailto:${settings.international_program_director_email}`}
                    className="hover:text-accent"
                  >
                    {settings.international_program_director_email}
                  </a>
                </ContactRow>
                <ContactRow icon={Building2} label={t('contact.info.campuses')}>
                  {getLocalizedSetting(settings, 'address', language)}
                </ContactRow>
              </ul>
            </div>
          </motion.div>

          <motion.form
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeInUp}
            onSubmit={handleSubmit}
            className="lg:col-span-3 rounded-2xl border border-border bg-white p-5 shadow-xl shadow-primary/5 sm:rounded-3xl sm:p-8"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              {['name', 'email', 'phone'].map((field) => (
                <div key={field}>
                  <label className="mb-1.5 block text-sm font-medium text-text/70">
                    {t(`contact.form.${field}`)}
                  </label>
                  <input
                    type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                    name={field}
                    value={form[field]}
                    onChange={handleChange}
                    required={field !== 'phone'}
                    className={inputClass}
                  />
                </div>
              ))}
              <div className={showBranchField ? '' : 'sm:col-span-2'}>
                <label className="mb-1.5 block text-sm font-medium text-text/70">
                  {t('contact.form.subject')}
                </label>
                <select
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  required
                  className={inputClass}
                >
                  <option value="">{t('contact.form.selectSubject')}</option>
                  {CONTACT_SUBJECT_KEYS.map((key) => (
                    <option key={key} value={key}>
                      {t(`contact.form.subjects.${key}`)}
                    </option>
                  ))}
                </select>
              </div>
              {showBranchField && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-text/70">
                    {t('contact.form.branch')}
                  </label>
                  <select
                    name="branch"
                    value={form.branch}
                    onChange={handleChange}
                    required={branchRequired}
                    className={inputClass}
                  >
                    <option value="">{t('contact.form.selectBranch')}</option>
                    {CONTACT_BRANCH_KEYS.map((key) => (
                      <option key={key} value={key}>
                        {t(`contact.form.branches.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-medium text-text/70">
                  {t('contact.form.message')}
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>

            {status && (
              <p
                className={`mt-4 text-sm ${status === 'success' ? 'text-green-600' : 'text-red-600'}`}
              >
                {t(`contact.form.${status}`)}
              </p>
            )}

            <Button
              type="submit"
              variant="accent"
              size="lg"
              disabled={submitting}
              className="mt-6 w-full sm:w-auto"
            >
              <Send className="h-4 w-4" />
              {submitting ? t('common.loading') : t('buttons.sendMessage')}
            </Button>
          </motion.form>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeInUp}
          className="mt-12"
        >
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3 className="text-2xl font-bold text-primary">{t('contact.info.mapTitle')}</h3>
              <p className="mt-1 text-text/65">{t('contact.info.mapSubtitle')}</p>
            </div>
            <a
              href={settings.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition-colors hover:text-primary"
            >
              {t('contact.info.openInMaps')} →
            </a>
          </div>
          <div className="overflow-hidden rounded-3xl border border-border shadow-xl shadow-primary/10">
            <iframe
              title={t('contact.info.mapTitle')}
              src={settings.google_maps_embed_url}
              className="h-[280px] w-full border-0 sm:h-[420px] md:h-[480px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </motion.div>
      </div>
    </section>
  )
}
